import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, AlertCircle, Search, Users, Ban, ShieldOff, UserX } from 'lucide-react';
import toast from '../lib/toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Avatar from '../components/Avatar';
import { BackHeader } from '../components/BackHeader';

/**
 * F18 roster UI (packet 2026-09-24): condo-wide member roster for residents.
 * Backend: GET /api/condos/:condoId/members — non-privileged viewers get the
 * LIMITED-FIELD branch ONLY: { user_id, name, role, unit_id, active }. No
 * email/phone/permissions/owner_email — do not render or request them.
 * Privacy filters are server-side (opt-out, blocks, minors omitted); an empty
 * list is a legitimate state (e.g. viewer opted out — bidirectional invisibility).
 * Blocks: POST /api/users/:id/block, DELETE /api/users/:id/block,
 * GET /api/users/me/blocks. DM 403 code BLOCKED is handled gracefully.
 * Masked entries (server-masked names like "J.P.") are rendered AS-IS: the FE
 * must NOT attempt to re-resolve or fetch identity for masked entries.
 */

interface RosterMember {
  user_id: string;
  name: string;
  role: string;
  unit_id?: string | null;
  active?: boolean | number;
}

interface BlockedEntry {
  user_id: string;
  name?: string | null;
  avatar_url?: string | null;
  created_at?: string;
}

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Administrador',
  sub_admin: 'Sub Admin',
  treasurer: 'Directivo',
  propietario: 'Propietario',
  residente: 'Residente',
  resident: 'Residente',
  familiar: 'Familiar',
  empleado: 'Personal',
  empleado_admin: 'Personal',
  security: 'Seguridad',
  mantenimiento: 'Mantenimiento',
  maintenance_staff: 'Mantenimiento',
};

const roleLabel = (role?: string) => (role && ROLE_LABELS[role]) || role || 'Miembro';

const errMessage = (err: any, fallback: string) => {
  const status = err?.response?.status;
  const code = err?.response?.data?.code;
  if (code === 'BLOCKED' || (status === 403 && /bloque/i.test(String(err?.response?.data?.error || '')))) {
    return 'No pueden comunicarse: existe un bloqueo entre estos usuarios';
  }
  if (status === 403) return 'No tienes acceso al directorio de este condominio';
  return err?.response?.data?.error || err?.message || fallback;
};

export default function RosterVecinos() {
  const { user, activeCondoId } = useAuth();
  const [members, setMembers] = useState<RosterMember[]>([]);
  const [blocked, setBlocked] = useState<BlockedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [confirmTarget, setConfirmTarget] = useState<RosterMember | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!activeCondoId) { setLoading(false); return; }
    try {
      setLoading(true);
      setError(null);
      const [rosterRes, blocksRes] = await Promise.all([
        api.get(`/condos/${activeCondoId}/members`),
        api.get('/users/me/blocks').catch(() => null), // blocks list is auxiliary
      ]);
      const rosterData = rosterRes.data?.data ?? rosterRes.data ?? {};
      setMembers(Array.isArray(rosterData?.members) ? rosterData.members : []);
      const blocksData = blocksRes?.data?.data ?? {};
      setBlocked(Array.isArray(blocksData?.blocks) ? blocksData.blocks : []);
    } catch (err: any) {
      setError(errMessage(err, 'Error al cargar el directorio'));
    } finally {
      setLoading(false);
    }
  }, [activeCondoId]);

  useEffect(() => { loadData(); }, [loadData]);

  const blockedIds = useMemo(() => new Set(blocked.map(b => String(b.user_id))), [blocked]);

  const filtered = useMemo(() => members.filter(m => {
    if (!search.trim()) return true;
    return String(m.name || '').toLowerCase().includes(search.toLowerCase().trim());
  }), [members, search]);

  const doBlock = async (target: RosterMember) => {
    setBusyId(target.user_id);
    setConfirmTarget(null);
    try {
      await api.post(`/users/${target.user_id}/block`);
      toast.success('Usuario bloqueado');
      await loadData();
    } catch (err: any) {
      toast.error(errMessage(err, 'No se pudo bloquear al usuario'));
    } finally {
      setBusyId(null);
    }
  };

  const doUnblock = async (target: BlockedEntry) => {
    setBusyId(String(target.user_id));
    try {
      await api.delete(`/users/${target.user_id}/block`);
      toast.success('Usuario desbloqueado');
      await loadData();
    } catch (err: any) {
      toast.error(errMessage(err, 'No se pudo desbloquear al usuario'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#36CCD8] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[60vh] gap-4">
        <AlertCircle className="w-12 h-12 text-red-400" />
        <p className="text-[var(--v-text-primary)] text-lg">{error}</p>
        <button onClick={loadData} className="px-4 py-2 bg-[#36CCD8] text-black font-semibold rounded-lg hover:bg-[#2DB8C4] transition">
          Reintentar
        </button>
      </div>
    );
  }

  if (!activeCondoId) {
    return (
      <div className="max-w-4xl mx-auto py-20 text-center">
        <Users className="w-12 h-12 text-gray-500 mx-auto mb-3" />
        <p className="text-[var(--v-text-secondary)]">Selecciona un condominio para ver el directorio de vecinos.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <BackHeader fallback="/" />
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--v-text-primary)] mb-1 flex items-center gap-2">
          <Users className="w-6 h-6 text-[#36CCD8]" /> Vecinos del condominio
        </h1>
        <p className="text-[var(--v-text-secondary)] text-sm">
          {members.length} {members.length === 1 ? 'vecino visible' : 'vecinos visibles'}
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
        <input
          type="text"
          placeholder="Buscar por nombre…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl text-[var(--v-text-primary)] placeholder-gray-500 focus:border-[#36CCD8] focus:outline-none transition"
        />
      </div>

      {/* Empty state — graceful when everyone opted out or viewer opted out */}
      {members.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <UserX className="w-12 h-12 text-gray-500" />
          <p className="text-[var(--v-text-primary)] text-lg">No hay vecinos visibles en el directorio</p>
          <p className="text-[var(--v-text-secondary)] text-sm max-w-md">
            Puede que hayas desactivado tu visibilidad en el directorio, que otros vecinos hayan
            optado por no aparecer, o que exista un bloqueo. Puedes cambiar tu visibilidad en
            Perfil → Privacidad del directorio.
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(member => {
            const isSelf = String(member.user_id) === String(user?.id);
            const isBlocked = blockedIds.has(String(member.user_id));
            return (
              <div
                key={member.user_id}
                className="bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl p-4 hover:border-[#36CCD8]/50 transition flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Masked entries arrive pre-masked from the API (e.g. "J.P.");
                      render the name exactly as provided — never re-resolve. */}
                  <Avatar name={member.name} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-[var(--v-text-primary)] truncate">{member.name}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#36CCD8]/10 text-[#36CCD8] font-medium">
                        {roleLabel(member.role)}
                      </span>
                    </div>
                    {/* Limited fields only: role + unit_id. No email/phone by design. */}
                    {member.unit_id ? (
                      <p className="text-xs text-[var(--v-text-secondary)] mt-1 truncate">Unidad: {member.unit_id}</p>
                    ) : null}
                  </div>
                </div>
                {!isSelf && (
                  isBlocked ? (
                    <button
                      onClick={() => doUnblock({ user_id: member.user_id, name: member.name })}
                      disabled={busyId === String(member.user_id)}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-[var(--v-border-light)] text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)] hover:bg-[var(--v-surface-3)] transition flex-shrink-0 disabled:opacity-50"
                    >
                      <ShieldOff className="w-4 h-4" /> Desbloquear
                    </button>
                  ) : (
                    <button
                      onClick={() => setConfirmTarget(member)}
                      disabled={busyId === String(member.user_id)}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-[var(--v-danger)]/40 text-[var(--v-danger)] hover:bg-[var(--v-danger)]/10 transition flex-shrink-0 disabled:opacity-50"
                      aria-label={`Bloquear a ${member.name}`}
                    >
                      <Ban className="w-4 h-4" /> Bloquear
                    </button>
                  )
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-center text-[var(--v-text-secondary)] py-8">No se encontraron vecinos con ese nombre.</p>
          )}
        </div>
      )}

      {/* Blocked list (persisted unblock control) */}
      {blocked.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-bold text-[var(--v-text-primary)] mb-3 flex items-center gap-2">
            <Ban className="w-5 h-5 text-[var(--v-danger)]" /> Usuarios bloqueados ({blocked.length})
          </h2>
          <div className="grid gap-2">
            {blocked.map(entry => (
              <div key={entry.user_id} className="bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar name={entry.name || ''} avatarUrl={entry.avatar_url || null} size={32} />
                  {/* API may already mask this name — display as-is. */}
                  <span className="text-[var(--v-text-primary)] truncate">{entry.name || 'Usuario Desconocido'}</span>
                </div>
                <button
                  onClick={() => doUnblock(entry)}
                  disabled={busyId === String(entry.user_id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-[var(--v-border-light)] text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)] hover:bg-[var(--v-surface-3)] transition disabled:opacity-50"
                >
                  <ShieldOff className="w-4 h-4" /> Desbloquear
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Block confirmation dialog */}
      {confirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setConfirmTarget(null)}>
          <div className="bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[var(--v-text-primary)] mb-2">¿Bloquear a {confirmTarget.name}?</h3>
            <p className="text-sm text-[var(--v-text-secondary)] mb-5">
              Dejarán de verse en el directorio y no podrán enviarse mensajes ninguno de los dos.
              Puedes deshacerlo cuando quieras desde esta página.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmTarget(null)} className="px-4 py-2 rounded-lg border border-[var(--v-border-light)] text-[var(--v-text-primary)]">Cancelar</button>
              <button onClick={() => doBlock(confirmTarget)} className="px-4 py-2 rounded-lg bg-[var(--v-danger)] text-white font-semibold">Bloquear</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
